import { type RefObject, useCallback, useEffect, useRef } from "react";

import type { AudioRecorderContainerRef } from "../../AudioRecorderContainer";
import type { RegisterActiveResponseCompletion } from "../types";

type Args = {
	recorderRef: RefObject<AudioRecorderContainerRef | null>;
	onRecordingComplete?: (uri: string | undefined) => void;
	isAutoRecord?: boolean;
	onRecordingEnded?: () => void;
	onResponseComplete?: () => void;
	registerActiveResponseCompletion?: RegisterActiveResponseCompletion;
};

const useAudioResponseCompletion = ({
	recorderRef,
	onRecordingComplete,
	isAutoRecord,
	onRecordingEnded,
	onResponseComplete,
	registerActiveResponseCompletion,
}: Args) => {
	const firedRef = useRef(false);

	const handleRecordingEnded = useCallback(() => {
		if (firedRef.current) {
			return;
		}
		firedRef.current = true;
		onRecordingEnded?.();
		onResponseComplete?.();
	}, [onRecordingEnded, onResponseComplete]);

	const handleRecordingComplete = useCallback(
		(uri: string | undefined) => {
			onRecordingComplete?.(uri);
			if (isAutoRecord) {
				handleRecordingEnded();
			}
		},
		[onRecordingComplete, isAutoRecord, handleRecordingEnded],
	);

	const completeActiveRecording = useCallback(async () => {
		if (!recorderRef.current?.isRecording) {
			return;
		}
		await recorderRef.current.stop();
		handleRecordingEnded();
	}, [recorderRef, handleRecordingEnded]);

	useEffect(
		() => registerActiveResponseCompletion?.(completeActiveRecording),
		[registerActiveResponseCompletion, completeActiveRecording],
	);

	return { handleRecordingEnded, handleRecordingComplete };
};

export default useAudioResponseCompletion;
